import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { useToast } from "@/components/base/Toast";
import { useStore } from "@/context/StoreContext";
import type { Employee, EmployeeRole, EmployeeStatus } from "@/types";
import EmployeeDetailsDrawer from "./components/EmployeeDetailsDrawer";
import EmployeeFilters from "./components/EmployeeFilters";
import EmployeeStats from "./components/EmployeeStats";
import EmployeesTable from "./components/EmployeesTable";

export default function EmployeesPage() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { employees, branches, setEmployeeStatus } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const branchFilter = searchParams.get("branch") ?? "all";
  const [roleFilter, setRoleFilter] = useState<EmployeeRole | "all">("all");
  const [statusFilter, setStatusFilter] = useState<EmployeeStatus | "all">(
    "all",
  );
  const [query, setQuery] = useState("");
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<Employee | null>(null);

  const activeEmployee = useMemo(
    () => employees.find((item) => item.id === detailsId) ?? null,
    [employees, detailsId],
  );

  const branchNames = useMemo(
    () =>
      branches.reduce<Record<string, string>>((acc, branch) => {
        acc[branch.id] = branch.name;
        return acc;
      }, {}),
    [branches],
  );

  const visible = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return employees.filter((employee) => {
      const matchBranch =
        branchFilter === "all" || employee.branchId === branchFilter;
      const matchRole = roleFilter === "all" || employee.role === roleFilter;
      const matchStatus =
        statusFilter === "all" || employee.status === statusFilter;
      const matchQuery =
        keyword === "" ||
        employee.name.toLowerCase().includes(keyword) ||
        employee.phone.includes(keyword);
      return matchBranch && matchRole && matchStatus && matchQuery;
    });
  }, [employees, branchFilter, roleFilter, statusFilter, query]);

  const openAdd = () => {
    const params = branchFilter === "all" ? "" : `?branch=${branchFilter}`;
    navigate(`/employees/new${params}`);
  };

  const openEdit = (employee: Employee) => {
    navigate(`/employees/${employee.id}/edit`);
  };

  const openPermissions = (employee: Employee) => {
    navigate(`/employees/${employee.id}/edit?focus=permissions`);
  };

  const openDetails = (employee: Employee) => {
    setDetailsId(employee.id);
  };

  const closeDrawer = () => setDetailsId(null);

  const confirmToggle = () => {
    if (!confirmTarget) return;
    const nextStatus: EmployeeStatus =
      confirmTarget.status === "active" ? "suspended" : "active";
    setEmployeeStatus(confirmTarget.id, nextStatus);
    show(
      nextStatus === "active"
        ? "تم إعادة تفعيل حساب الموظف"
        : "تم إيقاف حساب الموظف",
      nextStatus === "active" ? "success" : "warning",
    );
    setConfirmTarget(null);
  };

  const handleBranchFilterChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") {
      next.delete("branch");
    } else {
      next.set("branch", value);
    }
    setSearchParams(next, { replace: true });
  };

  if (employees.length === 0) {
    return (
      <div className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card px-8 py-20 text-center">
        <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-4">
          <i className="ri-team-line text-3xl"></i>
        </div>
        <h2 className="text-lg font-bold text-foreground-950">
          لا يوجد موظفون
        </h2>
        <p className="mt-2 text-sm text-foreground-500">
          أضف موظفي الفروع وحدد أدوارهم وصلاحياتهم.
        </p>
        <button
          type="button"
          onClick={openAdd}
          className="mt-5 inline-flex items-center gap-2 rounded-btn bg-primary-500 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          إضافة أول موظف
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <section className="yc-fade-up flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-white rounded-card border border-background-200 shadow-card px-5 py-4">
        <div>
          <h2 className="text-base font-bold text-foreground-950">
            الموظفون والصلاحيات
          </h2>
          <p className="mt-0.5 text-xs text-foreground-500">
            إدارة موظفي الفروع والأدوار والصلاحيات الممنوحة لهم
          </p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-primary-500 px-5 text-sm font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line text-lg"></i>
          إضافة موظف
        </button>
      </section>

      <EmployeeStats employees={employees} />

      <section className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
        <EmployeeFilters
          query={query}
          onQueryChange={setQuery}
          branch={branchFilter}
          onBranchChange={handleBranchFilterChange}
          branchOptions={branches}
          role={roleFilter}
          onRoleChange={setRoleFilter}
          status={statusFilter}
          onStatusChange={setStatusFilter}
        />
      </section>

      <section className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-foreground-950">
            قائمة الموظفين
          </h2>
          <span className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600">
            {visible.length} موظف
          </span>
        </div>
        <EmployeesTable
          employees={visible}
          branchNames={branchNames}
          onView={openDetails}
          onEdit={openEdit}
          onEditPermissions={openPermissions}
          onToggleStatus={setConfirmTarget}
        />
      </section>

      <p className="yc-fade-up flex items-center gap-2 text-xs text-foreground-500">
        <i className="ri-information-line"></i>
        يمكنك إدارة موظفي أي فرع، وربطهم بالأدوار، وتحديد الصلاحيات الممنوحة لكل
        موظف من هذه الصفحة أو من صفحة الفرع.
        <button
          type="button"
          onClick={() => navigate("/branches")}
          className="font-semibold text-primary-600 hover:underline cursor-pointer whitespace-nowrap"
        >
          الذهاب إلى الفروع
        </button>
      </p>

      <EmployeeDetailsDrawer
        open={detailsId !== null}
        employee={activeEmployee}
        branchName={
          activeEmployee ? branchNames[activeEmployee.branchId] ?? "غير محدد" : ""
        }
        onClose={closeDrawer}
        onEdit={openEdit}
        onEditPermissions={openPermissions}
        onToggleStatus={setConfirmTarget}
      />

      <ConfirmDialog
        open={confirmTarget !== null}
        title={
          confirmTarget?.status === "active"
            ? "إيقاف حساب الموظف؟"
            : "إعادة تفعيل حساب الموظف؟"
        }
        message={
          confirmTarget?.status === "active"
            ? "لن يتمكن الموظف من تسجيل الدخول أو استخدام صلاحيات الفرع حتى إعادة تفعيل حسابه."
            : "سيتمكن الموظف من تسجيل الدخول واستخدام الصلاحيات الممنوحة له مرة أخرى."
        }
        confirmLabel={
          confirmTarget?.status === "active" ? "إيقاف الحساب" : "إعادة التفعيل"
        }
        tone={confirmTarget?.status === "active" ? "danger" : "primary"}
        onConfirm={confirmToggle}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}